import * as React from 'react';

import Input, { InputProps } from '../../atoms/Input';

const CURRENCY_REGEX = /\D*(\d+|\d.*?\d)(?:,+(\d{1,3}))?\D*$/;

export interface Props extends Omit<InputProps, 'type'> {
  /** Currency value multiplied by 100 */
  value?: string;
  currency?: 'BRL';
  allowsDecimal?: boolean;
}

const CurrencyInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      currency = 'BRL',
      value,
      allowsDecimal = false,
      onChange,
      onFocus,
      ...props
    },
    ref,
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) ?? innerRef;

    /**
     * Formats currency with Intl
     */
    const formatCurrency = React.useCallback(
      (textValue: number | '') => {
        if (!textValue && textValue !== 0) return '';
        if (Number.isNaN(textValue)) return '';

        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(textValue);
      },
      [currency],
    );

    const [displayValue, setDisplayValue] = React.useState(
      formatCurrency(value ? parseFloat(value) / 100 : ''),
    );

    /**
     * Formats currency and updates cursor position
     */
    const handleChange = React.useCallback<NonNullable<InputProps['onChange']>>(
      (e) => {
        const currencyParts = CURRENCY_REGEX.exec(e.target.value);
        const integerPart = currencyParts?.[1]?.replaceAll(/\D/g, '') || '';
        const decimalPart = currencyParts?.[2];

        let rawValue: '' | number = '';

        if (integerPart) {
          if (allowsDecimal && decimalPart !== undefined) {
            rawValue = parseFloat(`${integerPart}.${decimalPart.slice(0, 2)}`);
          } else if (allowsDecimal && e.target.value.includes(',')) {
            // User started typing decimal but hasn't finished
            rawValue = parseFloat(`${integerPart}.`);
          } else {
            rawValue = parseFloat(`${integerPart}.00`);
          }
        }

        const nativeEvent = e.nativeEvent as InputEvent;

        // Reset value if deleting the last character
        if (
          nativeEvent.inputType.startsWith('deleteContent') &&
          rawValue === 0
        ) {
          rawValue = '';
        }

        const formattedValue = formatCurrency(rawValue);

        // Cursor
        const beforeCursor = e.target.value.slice(
          0,
          e.target.selectionStart || undefined,
        );
        const digitsBeforeCursor = beforeCursor.replace(/[^\d]/g, '').length;

        // Updates display/onChange
        setDisplayValue(formattedValue);
        e.target.value =
          rawValue !== '' ? String(Math.round(rawValue * 100)) : '';
        onChange?.(e);

        const justTypedComma =
          nativeEvent.inputType === 'insertText' && nativeEvent.data === ',';

        // Set Caret position
        window.requestAnimationFrame(() => {
          const inputEl = inputRef.current;
          if (!inputEl) return;

          let newCursor = 0;

          if (justTypedComma && formattedValue.includes(',')) {
            // Move cursor to the first digit after the comma
            newCursor = formattedValue.indexOf(',') + 1;
          } else {
            let digitCount = 0;
            let lastDigitPosition = 0;
            for (let i = 0; i < formattedValue.length; i += 1) {
              if (/\d/.test(formattedValue[i])) {
                digitCount += 1;
                lastDigitPosition = i;
              }
              if (digitCount === digitsBeforeCursor) {
                newCursor = i + 1;
                break;
              } else if (i === formattedValue.length - 1) {
                newCursor = lastDigitPosition + 1;
              }
            }
          }
          inputEl.setSelectionRange(newCursor, newCursor);
        });
      },
      [allowsDecimal, formatCurrency, inputRef, onChange],
    );

    // Keep state in sync with external value
    React.useEffect(() => {
      setDisplayValue(formatCurrency(value ? parseFloat(value) / 100 : ''));
    }, [formatCurrency, value]);

    return (
      <Input
        className={className}
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleChange}
        {...props}
      />
    );
  },
);

export default CurrencyInput;
